import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import styles from '../styles';
import { getVendors, clearVendors } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';

const VendorList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const vendors = useSelector(state => state.vendors);

  useEffect(() => {
    dispatch(getVendors());
    return () => {
      dispatch(clearVendors());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/vendors/${id}`);
      successNotification('Vendor deleted');
      dispatch(getVendors());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateVendor) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/vendors/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteVendor) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: sorter('email'),
        onFilter: onFilter('email'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Mobile',
        dataIndex: 'mobile',
        sorter: sorter('mobile'),
        onFilter: onFilter('mobile'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Address',
        dataIndex: 'address',
        sorter: sorter('address'),
        onFilter: onFilter('address'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'State',
        dataIndex: 'state',
        sorter: sorter('state'),
        onFilter: onFilter('state'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'GST No',
        dataIndex: 'gstNo',
        sorter: sorter('gstNo'),
        onFilter: onFilter('gstNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Contact Person',
        dataIndex: 'contactPerson',
        sorter: sorter('contactPerson'),
        onFilter: onFilter('contactPerson'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateVendor)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteVendor)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Vendor"
        moduleLink="/vendors/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={vendors}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

VendorList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(VendorList);
